import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiChevronRight } from 'react-icons/fi';
import { apiFetch } from '../utils/api';
import { showToast } from './NotificationTray';
import PageHero from './PageHero';
import ArchiveFolderChildren from './ArchiveFolderChildren';

// A month inside the Scan Archive. The month itself never holds photos and
// cannot be renamed, this is where users create the folders that do.
export default function ScanArchiveMonth() {
  const { monthId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const requestVersion = useRef(0);
  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    try {
      const res = await apiFetch(`/api/archive/months/${monthId}`);
      const body = await res.json();
      if (version !== requestVersion.current) return;
      if (res.ok) setData(body);
      else { setData(null); showToast(body.error || "Could not load month", "error"); }
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [monthId]);

  useEffect(() => { setData(null); setLoading(true); load(); return () => { requestVersion.current++; }; }, [load]);

  if (!loading && !data) return <p role="alert">Month unavailable. <Link to="/archive">Return to archive</Link></p>;

  if (loading || !data) {
    return (
      <div className="mobile-content-wrapper">
        <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />
      </div>
    );
  }

  const { month, year, folders } = data;

  return (
    <div className="mobile-content-wrapper">
      <PageHero eyebrow="Scan Archive" title={`${month.name} ${year?.name || ''}`}>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/archive" className="hover:text-blue-600">Archive</Link>
          <FiChevronRight size={14} />
          {year && <Link to={`/archive/year/${year.id}`} className="hover:text-blue-600">{year.name}</Link>}
          <FiChevronRight size={14} />
          <span className="text-gray-800 font-medium">{month.name}</span>
        </div>
      </PageHero>

      <ArchiveFolderChildren key={month.id} parent={month} month={month} folders={folders} onChange={load} />
    </div>
  );
}
